import axios from "axios";
import { attachMockInterceptor } from "./setupMockInterceptors";
import { refreshAccessToken, clearAuthAndRedirect } from "../utils/tokenUtils";
import { getStoredAdminReadOnly } from "../utils/adminReadOnlyStore";

// Token refresh state (shared across all instances)
let isRefreshing = false;
let failedQueue = [];

const isGamePassUrl = (url) =>
  Boolean(url?.includes("game-pass") || url?.includes("gamepass"));

const READ_ONLY_SAFE_METHODS = ["get", "head", "options"];

/**
 * Interim read-only enforcement for the Game Pass admin API — the ONE place
 * that carries this rationale (component-level gating just improves UX):
 * unlike trivelta-admin-services routes, `/gamepass/v2/admin/*` is a
 * cross-team service with no backend read-only enforcement yet, so a
 * read-only admin's valid token would mutate seasons/challenges/tasks.
 * Until the API-side fix lands, mutating gamepass requests are blocked
 * client-side with a 403 shaped like the trivelta middleware's, so existing
 * error handling renders it unchanged.
 */
const READ_ONLY_BLOCK_MESSAGE = "Access denied. Your role has read-only access.";

const buildReadOnlyBlockError = (config) => {
  const error = new Error(READ_ONLY_BLOCK_MESSAGE);
  error.config = config;
  error.isReadOnlyBlock = true;
  error.response = {
    status: 403,
    data: {
      message: READ_ONLY_BLOCK_MESSAGE,
      detail: `${config?.method?.toUpperCase()} ${config?.url} is not permitted for read-only roles`,
    },
  };
  return error;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const createPrivateAdminPanelInstance = (
  bearerToken,
  dashboardBaseUrl = false
) => {
  const instance = axios.create({
    baseURL: dashboardBaseUrl
      ? process.env.REACT_APP_ADMIN_DASHBOARD_BACKEND_BASE_URL
      : process.env.REACT_APP_ADMIN_PANEL_BACKEND_BASE_URL,
    transformResponse: [
      (data) => {
        if (typeof data === 'string') {
          try {
            // Replace JavaScript-specific values that are not valid JSON
            const sanitized = data
              .replace(/:\s*NaN/g, ': null')
              .replace(/:\s*Infinity/g, ': null')
              .replace(/:\s*-Infinity/g, ': null')
              .replace(/:\s*undefined/g, ': null');

            return JSON.parse(sanitized);
          } catch (e) {
            console.warn('Failed to parse response as JSON:', e);
            return data;
          }
        }
        return data;
      },
    ],
  });

  // Add a request interceptor
  instance.interceptors.request.use(
    (config) => {
      if (
        getStoredAdminReadOnly() &&
        isGamePassUrl(config?.url) &&
        !READ_ONLY_SAFE_METHODS.includes(config?.method?.toLowerCase())
      ) {
        return Promise.reject(buildReadOnlyBlockError(config));
      }

      if (bearerToken) {
        config.headers.Authorization = `Bearer ${bearerToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  attachMockInterceptor(instance);

  // Add response interceptor AFTER mock to handle string responses with invalid JSON values
  // and 401 errors with automatic token refresh
  instance.interceptors.response.use(
    (response) => {
      if (typeof response.data === 'string') {
        try {
          // Replace JavaScript-specific values that are not valid JSON
          const sanitized = response.data
            .replace(/:\s*NaN/g, ': null')
            .replace(/:\s*Infinity/g, ': null')
            .replace(/:\s*-Infinity/g, ': null')
            .replace(/:\s*undefined/g, ': null');
          
          response.data = JSON.parse(sanitized);
        } catch (e) {
          console.warn('Failed to parse response as JSON:', e);
        }
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const isGamePassRequest = isGamePassUrl(originalRequest?.url);

      // Handle 401 errors with token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Game Pass endpoints still often require the temporary Game Pass token
        // instead of the regular PAM staff token. A 401 here should not tear
        // down the entire admin session; let the caller surface the failure
        // inline so the user can add/update the Game Pass token and retry.
        if (isGamePassRequest) {
          return Promise.reject(error);
        }

        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newTokens = await refreshAccessToken();
          processQueue(null, newTokens.AccessToken);
          originalRequest.headers.Authorization = `Bearer ${newTokens.AccessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearAuthAndRedirect(); // Redirect to login on refresh failure
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export default createPrivateAdminPanelInstance;